/* eslint-disable no-nested-ternary */
import get from 'lodash/get';
import ScriptLoader from 'yoda-core-components/lib/helpers/ScriptLoader/ScriptLoader';
import React, { useEffect, useState } from 'react';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import isEmpty from 'lodash/isEmpty';
import {
    TrueToSizeResponse,
    TrueToSizeSmallMsg,
    TrueToSizeLargeMsg,
    TrueToSizeMsg,
    TrueToSizeParam,
} from '../../common/ProductConstants';

const TrueToSizeIndicator = (props) => {
    const ppId = get(props, 'ppId', '');
    const modelHeight = get(props, 'modelHeight', '');
    const modelWearingSize = get(props, 'modelWearingSize', '');
    const enableModelSizing = get(props, 'enableModelSizing', false);
    const isModelSizingAvailable = modelHeight?.toString() && modelWearingSize?.toString();
    const [isScriptLoaded, setScriptLoaded] = useState(false);
    const [trueToFitSizeString, setTrueToFitSizeString] = useState('');
    const { clientId, staging } = TrueToSizeParam;
    const modelSizingLabel =
        enableModelSizing &&
        isModelSizingAvailable &&
        `Model is ${modelHeight || ''} and wearing US ${modelWearingSize || ''}`;

    const sectionClass = dt([
        'w-full',
        'text-sm',
        'flex',
        'lg:mt-6',
        'lg:mb-4',
        'smMd:my-6',
        'flex-row',
        'smMd:flex-col',
        'smMd:gap-2',
        'items-center',
        'smMd:items-start',
        'font-sans',
    ]);

    useEffect(() => {
        !isScriptLoaded && loadScript(clientId, staging, { autoCalculate: true });
    }, []);

    /* istanbul ignore next */
    const loadScript = (r, e, o) => {
        const w = window;
        let t;
        w.tfcapi = t =
            w.tfcapi ||
            function () {
                t.q = t.q || [];
                t.q.push(arguments);
            };
        t('init', { storeKey: r, environment: e, ...o });
        const srcUrl = `https://${r}-cdn${
            e === 'dev' || e === 'staging' ? `.${e}` : ''
        }.truefitcorp.com/fitrec/${r}/js/tf-integration.js`;
        const trueToFitSizeProps = {
            src: srcUrl,
            async: true,
            defer: false,
            onSuccess: () => {
                registerHooks();
            },
        };
        ScriptLoader.load(trueToFitSizeProps);
    };

    /* istanbul ignore next */
    const registerHooks = () => {
        const argsObj = { styleId: ppId, locale: 'en_US' };
        window.tfcapi &&
            window.tfcapi('getProductInfo', argsObj, (productInfo) => {
                if (productInfo) {
                    const APIGuidanceValue = productInfo?.universalGuidance?.guidance;
                    setTrueToFitSizeString(APIGuidanceValue);
                }
            });
        setScriptLoaded(true);
    };

    const applyClassToIndicator = () => {
        const {
            RunsOneSizeStepLarge,
            MayRunOneSizeStepLarge,
            RunsTrueToSizeLarge,
            RunsTrueToSizeSmall,
            MayRunOneSizeStepSmall,
            RunsOneSizeStepSmall,
            RunsTrueToSize,
        } = TrueToSizeResponse;

        /* istanbul ignore next */
        switch (trueToFitSizeString) {
            case RunsOneSizeStepLarge:
            case MayRunOneSizeStepLarge:
            case RunsTrueToSizeLarge: {
                return {
                    trueSizeLabel: TrueToSizeLargeMsg.label,
                    class: dt(['ml-[75%]']),
                    msgLabel: modelSizingLabel || TrueToSizeLargeMsg.message,
                };
            }
            case RunsTrueToSizeSmall:
            case MayRunOneSizeStepSmall:
            case RunsOneSizeStepSmall: {
                return {
                    trueSizeLabel: TrueToSizeSmallMsg.label,
                    class: dt(['ml-[25%]']),
                    msgLabel: modelSizingLabel || TrueToSizeSmallMsg.message,
                };
            }
            case RunsTrueToSize:
                return {
                    trueSizeLabel: TrueToSizeMsg.label,
                    class: dt(['ml-[50%]']),
                    msgLabel: modelSizingLabel || TrueToSizeMsg.message,
                };
            default:
                return {};
        }
    };
    const componentDetails = applyClassToIndicator();
    const iscomponentDetailsTrueFit = componentDetails?.msgLabel === 'This item runs true to size.';
    return (
        <>
            {!isEmpty(componentDetails) ? (
                <section
                    key="ttSizeIndicator"
                    className={sectionClass}
                    data-automation-id="tfSizeContainer"
                >
                    <div
                        className={dt(['w-fit', 'text-left', 'leading-5'])}
                        data-automation-id="tf_label"
                    >
                        <b className={dt(['font-semibold'])}>Fit – </b>
                        {componentDetails.trueSizeLabel}
                    </div>
                    <div
                        className={dt([
                            'w-32',
                            'relative',
                            'mt-1',
                            'ml-2',
                            'smMd:ml-0',
                            'mr-6',
                            'smMd:mr-0',
                        ])}
                        data-automation-id="tf_indicator"
                    >
                        <div
                            className={dt([
                                'border-2',
                                'border-solid',
                                'border-gray-15',
                                '-mt-[2px]',
                            ])}
                        />
                        <span
                            className={`${dt([
                                'h-[10px]',
                                '-mt-[7px]',
                                'absolute',
                                'rounded-[1px]',
                                'border-[3px]',
                                'border-solid',
                                'border-gray-70',
                            ])} ${componentDetails.class}`}
                        />
                    </div>
                    {!iscomponentDetailsTrueFit && (
                        <div
                            className={dt([
                                'w-fit',
                                'text-left',
                                'leading-5',
                                isModelSizingAvailable && 'smMd:mt-2',
                            ])}
                            data-automation-id="tf_message"
                        >
                            {componentDetails.msgLabel}
                        </div>
                    )}
                </section>
            ) : enableModelSizing && isModelSizingAvailable ? (
                <section className={sectionClass}>
                    <div className={dt(['w-fit', 'text-left', 'leading-5'])}>
                        {modelSizingLabel}
                    </div>
                </section>
            ) : null}
        </>
    );
};

export default TrueToSizeIndicator;
